import { motion, useScroll, useTransform } from "framer-motion";
import { type ReactNode, useRef } from "react";
import clsx from "clsx";
import { MuxScreen } from "./MuxScreen";

type Props = {
  id?: string;
  videoId: string;
  children: ReactNode;
  /** opacity-darken overlay 0..1 */
  darken?: number;
  /** parallax distance in px for the video layer */
  parallax?: number;
  /** vertical padding tailwind classes */
  padding?: string;
  /** min-height tailwind class */
  minH?: string;
  /** show aurora wash */
  aurora?: boolean;
  /** video frame mode: "background" (covers section, can pixelate) or "contained" (fixed aspect, sharp) */
  mode?: "background" | "contained";
  /** content placement when mode=contained */
  align?: "left" | "right" | "center";
  className?: string;
};

/**
 * Section with a Mux video. Two modes:
 *
 *  - "contained" (default) → video sits as a fixed-aspect rounded panel
 *    (60% width on desktop) so pixels render at their native resolution.
 *    Content sits beside or below. The video still has parallax.
 *
 *  - "background" → video is full-bleed behind content. Use only for
 *    sections where you want immersion and accept some scaling.
 *
 * Parallax: framer-motion drives the video Y-position and scale on scroll.
 * Defaults are aggressive enough to be obvious (±260px on desktop).
 */
export function VideoSection({
  id, videoId, children,
  darken = 0.4, parallax = 260, aurora = true,
  padding = "py-24 md:py-32", minH = "min-h-[100svh]",
  mode = "contained", align = "left",
  className,
}: Props) {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [-parallax, parallax]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.04, 1.0, 1.04]);

  if (mode === "background") {
    return (
      <section
        id={id}
        ref={ref}
        className={clsx("relative overflow-hidden", minH, padding, className)}
      >
        <motion.div
          aria-hidden
          style={{ y, scale, position: "absolute", inset: "-10%", zIndex: 0 }}
        >
          <MuxScreen
            id={videoId}
            className="absolute inset-0 w-full h-full"
            filter="saturate(1.1) contrast(1.05)"
          />
        </motion.div>
        <div
          aria-hidden
          style={{
            position: "absolute", inset: 0, pointerEvents: "none", zIndex: 1,
            background: `linear-gradient(180deg,
              rgba(4,8,11,${Math.max(0, darken - 0.2)}) 0%,
              rgba(4,8,11,${darken}) 50%,
              rgba(4,8,11,${Math.min(1, darken + 0.25)}) 100%)`,
          }}
        />
        {aurora && (
          <div
            aria-hidden
            style={{
              position: "absolute", inset: 0, pointerEvents: "none", zIndex: 1,
              mixBlendMode: "overlay", opacity: 0.6,
              background:
                "radial-gradient(60vmax 50vmax at 25% 25%, rgba(64,176,194,0.30), transparent 60%), radial-gradient(60vmax 50vmax at 80% 80%, rgba(61,220,151,0.20), transparent 60%)",
            }}
          />
        )}
        <div className="relative z-10 mx-auto max-w-7xl px-5 md:px-10">
          {children}
        </div>
      </section>
    );
  }

  // CONTAINED mode — fixed aspect, no stretching → no pixelation
  return (
    <section
      id={id}
      ref={ref}
      className={clsx("relative", padding, className)}
    >
      <div className="mx-auto max-w-7xl px-5 md:px-10 grid lg:grid-cols-2 gap-12 items-center">
        {align === "right" && <div>{children}</div>}

        <motion.div
          style={{ y, scale }}
          className="relative w-full aspect-[16/10] rounded-[1.5rem] overflow-hidden shadow-[0_30px_80px_-30px_rgba(64,176,194,0.45)] border border-white/10"
        >
          <MuxScreen
            id={videoId}
            className="absolute inset-0 w-full h-full"
            filter="saturate(1.1) contrast(1.05)"
          />
          <div
            aria-hidden
            style={{
              position: "absolute", inset: 0, pointerEvents: "none",
              background: `linear-gradient(180deg,
                rgba(4,8,11,${Math.max(0, darken - 0.25)}) 0%,
                rgba(4,8,11,0) 35%,
                rgba(4,8,11,0) 65%,
                rgba(4,8,11,${darken}) 100%)`,
            }}
          />
          {aurora && (
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none mix-blend-overlay opacity-50"
              style={{
                background:
                  "radial-gradient(60% 60% at 30% 30%, rgba(64,176,194,0.35), transparent 60%), radial-gradient(60% 60% at 80% 80%, rgba(61,220,151,0.25), transparent 60%)",
              }}
            />
          )}
        </motion.div>

        {align !== "right" && <div>{children}</div>}
      </div>
    </section>
  );
}
